import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { UpdateClientsCacheApi } from '@/pages/Clients/api/clients.api';
import { useDialogStore } from '@/store';

// Toast configuration for consistent styling
const toastConfig = {
  success: {
    duration: 3000,
  },
  error: {
    duration: 4000,
  },
};

export function useUpdateClientsCache() {
  const { openGlobalLoading, closeGlobalLoading, globalLoading } =
    useDialogStore();
  const isGlobalLoading = globalLoading.isLoading;

  const queryClient = useQueryClient();

  const updateClientsCacheMutation = useMutation({
    mutationFn: UpdateClientsCacheApi,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['AllClients'] }),
        queryClient.invalidateQueries({ queryKey: ['GroupItemData'] }),
        queryClient.invalidateQueries({ queryKey: ['allItems'] }),
      ]);
      closeGlobalLoading();
      toast.success(
        'تم تحديث العملاء من البرنامج الرئيسي',
        toastConfig.success,
      );
    },
    onError: (err: AxiosError<{ message?: string }>) => {
      closeGlobalLoading();
      toast.error(err?.response?.data?.message || 'خطأ', toastConfig.error);
    },
  });

  // Use a ref to avoid recreating the handler on every render
  const handlerRef = useRef<((event: KeyboardEvent) => Promise<void>) | null>(
    null,
  );

  // Memoize the handler creation using useCallback
  handlerRef.current = useCallback(
    async (event: KeyboardEvent) => {
      if (event.key === 'F4' && event.shiftKey && !isGlobalLoading) {
        openGlobalLoading();
        await updateClientsCacheMutation.mutateAsync();
      }
    },
    [openGlobalLoading, isGlobalLoading, updateClientsCacheMutation],
  );

  useEffect(() => {
    // Use a stable reference to the handler
    const handleKeyPress = (event: KeyboardEvent) => {
      handlerRef.current?.(event);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []); // Empty dependency array since we're using refs

  return { updateClientsCacheMutation };
}
